import { message } from 'antd'
import type { Reducer, Effect } from 'umi'
import { produce } from 'immer'
import priCenterSdk from '@xiaoduo/pricentersdk'
import { init, getPages } from '@xiaoduo/x-data/utils'
import OSS from 'ali-oss'
import { getMenuState } from '@/utils/route'
import * as services from '@/services/global'

init({
  debug:
    window.location.host?.startsWith('localhost') ||
    window.location.host?.startsWith('training-dev-lane'),
})
export interface Route {
  icon?: string
  name?: string
  path?: string
  component?: string
  hideInMenu?: boolean
  routes?: Route[]
}
export interface Menu {
  icon?: string
  name?: string
  path?: string
  children?: Menu[]
}

export interface ShopOption extends ShopItem {
  value: string
  label: string
  children?: ShopOption[]
}
export interface departmentOption extends DepartmentItem {
  value: string
  label: string
  children?: departmentOption[]
}

export type GlobalModelState = {
  routes: {
    [key: string]: Route
  }
  breadcrumbs: {
    [key: string]: Route
  }
  menus: Menu[]
  firstRoute: Route
  employeeList: any
  xdata: IPage[]
  ossInfo: OssInfoType
  videoSrc: string
  videoShow: boolean
  runJoyride: boolean
  joyrideId: number
  stepIndex: number
  helpCenter: boolean
  showHelpCenter: boolean
  taskDrawerVisible: boolean
  taskAlertModalVisible: boolean
  uploadOperateType: string
  showItem: any
  showVisible: boolean
  showUserModal: boolean
  showPasswordModal: boolean
  showDataModal: boolean
  showCarouselModal: boolean
  showDataDetails: boolean
  isFirst: boolean
  showAIChat: boolean
  aiChatWeight: number
  html: string
}

export interface IPage {
  id: string
  name: string
  primary_pages?: IPage[]
  secondary_pages?: SecondPages[]
}

export interface SecondPages {
  hidden: boolean
  id: string
  layout: string
  name: string
}

export interface UserResult {
  users: [
    {
      avatar: string
      id: string
      name: string
      role: {
        name: string
      }
      tenant_id: string
    }
  ]
}

export interface OssInfoType {
  accessKeyId: string
  accessKeySecret: string
  stsToken: string
  region: string
  bucket: string
  expireTime?: number
}

export type GlobalModelType = {
  namespace: 'global'
  state: GlobalModelState
  effects: {
    initData: Effect
    updateMenu: Effect
    fetchEmployees: Effect
    fetchOssInfo: Effect
    simpleUpload: Effect
    fetchData: Effect
    fetchImgParse: Effect
  }
  reducers: {
    setState: Reducer<GlobalModelState>
  }
  subscriptions: any
}

const TOKEN_EXPIRE_TIME = 1800 * 1000 - 3
const FILE_PATH = 'user_upload/'
const DEFAULT_REGION = 'oss-cn-hangzhou'
const DEFAULT_BUCKET = 'xd-mp'
const DEFAULT_URL = `https://${DEFAULT_BUCKET}.${DEFAULT_REGION}.aliyuncs.com`
const MULTIPART_SIZE = 100

const UserModel: GlobalModelType = {
  namespace: 'global',
  state: {
    // 路由相关
    routes: {},
    breadcrumbs: {},
    menus: [],
    firstRoute: {},
    employeeList: [],
    xdata: [],
    ossInfo: {
      accessKeyId: '',
      accessKeySecret: '',
      stsToken: '',
      region: DEFAULT_REGION,
      bucket: DEFAULT_BUCKET,
    },
    videoShow: false,
    videoSrc: '',
    runJoyride: false,
    joyrideId: null,
    stepIndex: 0,
    helpCenter: false,
    showHelpCenter: false,
    taskDrawerVisible: false,
    taskAlertModalVisible: false,
    uploadOperateType: '',
    showItem: null,
    showVisible: false,
    showUserModal: false,
    showPasswordModal: false,
    showDataModal: false,
    showCarouselModal: false,
    showDataDetails: false,
    isFirst: false,
    showAIChat: false,
    aiChatWeight: 800,
    html: '',
  },

  effects: {
    *initData(action, { call, put }) {
      // 取xdata
      let xdata = []
      const xdataRes = yield call(getPages, 'barracks')
      // ?? 是默认值
      xdata =
        xdataRes?.items?.map((item: IPage) => {
          const munuPath = item.name === '数据监控' ? '/data' : `/${item.id}`
          return {
            ...item,
            path: munuPath,
            routes: item.primary_pages
              ?.filter((i) => (i.secondary_pages || [])?.length > 0)
              .map((i) => ({ path: `${munuPath}/${i.id}`, ...i })),
          }
        }) ?? []

      yield put({
        type: 'setState',
        payload: { xdata },
      })
      yield put({ type: 'fetchEmployees' }) // 获取客服姓名
    },

    *updateMenu(action, { put, select }) {
      const {
        features,
        role: { name: roleName },
      } = yield select((store) => store.user)
      const { menus, breadcrumbs, routes, firstRoute } = getMenuState(roleName, features)

      // 计算所有的权限
      yield put({
        type: 'setState',
        payload: { menus, routes, breadcrumbs, firstRoute },
      })
    },
    // 获取客服姓名
    *fetchEmployees({ payload }, { call, select, put }) {
      // const currentUserId = yield select((state) => state.user.id)
      payload = {
        customer_service: true, // 是否为一线客服
        status: 1, // 1 表示在职员工
      }
      const { data } = yield call(services.fetchEmployees, payload)
      const employeeList = (data?.users || []).map((item) => {
        return {
          ...item,
          value: item.id,
          label: item.username,
        }
      })

      yield put({
        type: 'setState',
        payload: {
          employeeList,
        },
      })
    },
    *fetchOssInfo(_, { call, select, put }) {
      const ossInfo = yield select((store) => store.global.ossInfo)
      if (ossInfo.expireTime !== undefined && ossInfo.expireTime > new Date()) return

      const res = yield call(services.fetchOssToken)
      if (res?.code === 0) {
        const newInfo = {
          ...ossInfo,
          accessKeyId: res.data.accessKeyId,
          accessKeySecret: res.data.accessKeySecret,
          stsToken: res.data.securityToken,
          expireTime: Number(new Date()) + TOKEN_EXPIRE_TIME,
        }
        yield put({
          type: 'setState',
          payload: {
            ossInfo: {
              ...newInfo,
            },
          },
        })
      }
    },

    *simpleUpload({ payload }, { call, select, put }) {
      const { file } = payload
      yield yield put({ type: 'fetchOssInfo' })
      const ossInfo = yield select((store) => store.global.ossInfo)
      const dotIndex = (file.name as string).lastIndexOf('.')
      const originName = file.name.slice(0, dotIndex)
      const suffix = file.name.slice(dotIndex, file.name.length)
      const filename = `${originName}_${new Date().getTime()}${suffix}`
      const client = new OSS({
        ...ossInfo,
      })
      const options = {
        mime: file.type,
        timeout: 120 * 1000,
      }

      try {
        // 文件过大使用分片上传
        if (file.size > MULTIPART_SIZE * 1024 * 1024) {
          yield client.multipartUpload(`${FILE_PATH}${filename}`, file, {
            ...options,
            parallel: 5,
            partSize: 1024 * 1024 * 5,
          })
        } else {
          yield client.put(`${FILE_PATH}${filename}`, file, options)
        }

        return {
          url: `${DEFAULT_URL}/${FILE_PATH}${encodeURIComponent(filename)}`,
          name: filename,
          size: file.size,
        }
      } catch (e) {
        message.error('上传失败！')
      }
    },

    *fetchData(_, { call, put }) {
      const response = yield call(services.fetchData)
      return response.data?.traffic_num
    },

    *fetchImgParse({ payload }, { call, put }) {
      const result = yield call(services.fetchImgParse, payload)
      if (result.code === 0) {
        return result.data.desc
      } else {
        throw new Error(result.message || '图片解析失败了！')
      }
    },
  },

  reducers: {
    setState: produce((state, action) => ({
      ...state,
      ...action.payload,
    })),
  },
  subscriptions: {},
}

export default UserModel
